import { BASE_LEVEL } from "@sins2-atlas/compute";
import type { Unit } from "@sins2-atlas/game-data";
import { NO_POINTS, pointedAbilities, trimAllocation } from "./ability-points";
import { type StatFrame, unhonouredPoints, withPoints } from "./frame";
import { abilityPointsTrimNote } from "./frame-labels";
import { type LoadoutTransition, reconcileLoadout } from "./loadout-index";

// The hull's own reconcile: the one door every control on an entity page and in a comparison
// column writes its new frame through (ADR 0012, ADR 0015 decision 2). Two per-hull levers
// can be invalidated by a change to another axis, and each is put right here rather than at
// the controls, which is what makes them agree however the reader reaches them:
//
//   - the LOADOUT against the research axis (Issue 46) — unticking research takes off what it
//     bought, and a hull whose slot count fell is refitted (lib/loadout-index.ts);
//   - the ALLOCATION against the Level (Issue 61) — lowering the Level trims it to the
//     nearest one the game could reach (lib/ability-points.ts).
//
// In that order, because the Loadout's reconcile can move the research axis and nothing about
// the allocation depends on it. Whatever either did is said out loud in one notice: a lever
// that quietly loses what the reader chose is one they cannot trust.

/** What one frame change did, in the site's own words, or undefined when it did exactly what
 * the reader asked and nothing else.
 *
 * Two notes rather than one, because two surfaces say them and only one of them speaks for
 * every lever. `note` is the whole of it and belongs to the page's own line under the
 * controls. `loadoutNote` is the Loadout's half alone, and is all the Ship Items panel may
 * repeat in its header while it is open over that line: the panel speaks AS the Loadout, so
 * an Ability Points trim there would attribute the Level control's doing to the Loadout
 * (found in review 2026-09-16 — a Level drop's "Assault Specialization removed." was
 * appearing in the Ship Items header). */
export interface FrameTransition {
  frame: StatFrame;
  note?: string;
  loadoutNote?: string;
}

/** The frame a control's change settles to, and what settling it cost.
 *
 * `frame` is the one the page is SHOWING and `next` the one the control wrote — the trim
 * needs both, because "the allocation just became illegal" and "the URL named an illegal one
 * and the page has been reporting it since it loaded" are different situations with
 * different answers (see trimPoints). */
export function reconcileFrame(unit: Unit, frame: StatFrame, next: StatFrame): FrameTransition {
  const loadout = reconcileLoadout(unit, next);
  const points = trimPoints(unit, frame, loadout.frame);
  const notes = [loadout.note, points.note].filter((note) => note !== undefined);
  return {
    frame: points.frame,
    ...(notes.length === 0 ? {} : { note: notes.join(" ") }),
    ...(loadout.note === undefined ? {} : { loadoutNote: loadout.note }),
  };
}

/** The allocation trimmed to the nearest one this hull could reach at the frame's Level, and
 * the sentence naming what that took (ADR 0015 decision 2). Lowering the Level is what
 * reaches it: raising one widens both the gates and the pool, so a legal allocation stays
 * legal and nothing is said.
 *
 * An allocation the page was ALREADY not honouring — issue 60's cold `?points=` fallback: a
 * link naming an allocation the hull could not have bought folds nothing, reads with nothing
 * spent and is quoted back by the notice — is never trimmed into a build the reader did not
 * ask for (ADR 0015 decision 1). While the Level stands it is carried as it arrived, so the
 * notice keeps quoting it. When the reader moves the Level it is DROPPED to nothing spent
 * instead: the reader has just said what they meant, exactly as choosing a Level drops a
 * `?level=` token that was no level (lib/frame.ts, withLevel) — and carrying it would let a
 * raise that widened the gates put points in force the reader never spent (review
 * 2026-09-10). Nothing is said, because nothing was ever spent. */
function trimPoints(unit: Unit, frame: StatFrame, next: StatFrame): LoadoutTransition {
  const allocation = next.points;
  if (allocation === undefined || allocation.length === 0) return { frame: next };
  if (unhonouredPoints(unit, frame) !== undefined) {
    return { frame: frame.level === next.level ? next : withPoints(next, NO_POINTS) };
  }
  const level = next.level ?? BASE_LEVEL;
  const abilities = pointedAbilities(unit.abilities);
  const trim = trimAllocation(allocation, abilities, level);
  if (trim.trimmed.length === 0) return { frame: next };
  const note = abilityPointsTrimNote(
    level,
    trim.trimmed.map((change) => ({ name: abilities[change.index]?.name ?? "", to: change.to })),
  );
  return { frame: withPoints(next, trim.allocation), ...(note === undefined ? {} : { note }) };
}
